import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import * as batcheservice from '../../../service/product-batch.service';
import { ProductAndBatchId, ProductBatch, ProductBatchRequest } from '../../../service/model/store.model';
import { NotificationType } from '../../../shared/enums';
import { showNotification } from '../../alerts/alert.slice';
import { getAxiosErrorMessage } from '../../../service/axios/axios.call.service';
import { fetchProducts, fetchProductSummary } from '../products/product.slice';

export interface Batchestate {
    batches: ProductBatch[];
    isLoading: boolean;
}

export const initialState: Batchestate = {
    batches: [],
    isLoading: false,
};

export const fetchBatches = createAsyncThunk<ProductBatch[], number>(
    'batches/all',
    async (id, { rejectWithValue }) => {
        return batcheservice.getBatches(id).catch((errors) => {
            return rejectWithValue(errors);
        });
    }
);

export const createBatch = createAsyncThunk<ProductBatch, ProductBatchRequest>(
    'batches/create',
    async (request, { rejectWithValue, dispatch }) => {
        return batcheservice
            .createBatch(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Batch created successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchProducts());
                dispatch(fetchProductSummary());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const updateBatch = createAsyncThunk<ProductBatch, ProductBatchRequest>(
    'batches/update',
    async (request, { rejectWithValue, dispatch }) => {
        return batcheservice
            .updateBatch(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Batch updated successfully',
                        type: NotificationType.Success,
                    })
                );                
                dispatch(fetchProducts());
                dispatch(fetchProductSummary());
                dispatch(fetchBatches(request.productId));
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const deleteBatch = createAsyncThunk<number, ProductAndBatchId>(
    'batches/delete',
    async (request, { rejectWithValue, dispatch }) => {
        return batcheservice
            .deleteBatch(request.batchId)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Batch deleted successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchProducts());
                dispatch(fetchProductSummary());
                dispatch(fetchBatches(request.productId));
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

const batcheslice = createSlice({
    name: 'batches',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBatches.pending, (state) => {})
            .addCase(
                fetchBatches.fulfilled,
                (state, action: PayloadAction<ProductBatch[]>) => {
                    state.batches = action.payload;
                }
            )
            .addCase(fetchBatches.rejected, (state) => {})
            .addCase(createBatch.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createBatch.fulfilled,
                (state, action: PayloadAction<ProductBatch>) => {
                    state.isLoading = false;
                }
            )
            .addCase(createBatch.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateBatch.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateBatch.fulfilled,
                (state, action: PayloadAction<ProductBatch>) => {
                    state.isLoading = false;
                }
            )
            .addCase(updateBatch.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteBatch.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteBatch.fulfilled,
                (state, action: PayloadAction<number>) => {
                    state.isLoading = false;
                }
            )
            .addCase(deleteBatch.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const selectBatches = (state: RootState) => state.productBatches.batches;
export const selectIsLoading = (state: RootState) => state.productBatches.isLoading;

export default batcheslice.reducer;
